import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, Renderer2 } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { ApiService } from 'src/app/shared/services/api.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { UserService } from 'src/app/shared/services/user.service';
import { environment } from 'src/environments/environment';
import { ToastService } from 'src/app/shared/services/toast.service';

@Component({
  selector: 'app-email-templates',
  templateUrl: './email-templates.component.html',
  styleUrls: ['./email-templates.component.scss']
})
export class EmailTemplatesComponent implements OnInit {
  emailTemplates: any[] = [];
  selectedTemplate: any = null;
  templateKeys: any[] = [];
  cursorPosition: { field: string; position: number } | null = null;
  headerBlocks: Array<{ type: 'text' | 'key'; content: string }> = [];
  bodyBlocks: Array<{ type: 'text' | 'key'; content: string }> = [];
  private currentField: string | null = null;
  private savedRange: Range | null = null;


  constructor(
    private renderer: Renderer2,
    private _router: Router,
    private _activeRoute: ActivatedRoute,
    private _formBuilder: FormBuilder,
    private _http: HttpClient,
    private _dialogService: DialogService,
    private _loaderService: LoaderService,
    private _userService: UserService,
    private _toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loadEmailTemplates();
  }
  setCursorField(field: string): void {
    this.currentField = field;

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      this.savedRange = selection.getRangeAt(0).cloneRange(); // Save the cursor position
    }
  }
  private createApiService(): ApiService {
    const apiService = new ApiService(this._http);
    apiService['_baseUrl'] = environment.API_URL;
    return apiService;
  }

  async loadEmailTemplates(): Promise<void> {
    try {
      this.emailTemplates = await this.getEmailTemplates();
      this._toastService.success('Email templates loaded successfully!', 'Success');
    } catch (error) {
      this._toastService.error('Failed to load email templates.', 'Error');
      console.error('Error loading email templates:', error);
    }
  }

  selectTemplate(template: any): void {
    this.selectedTemplate = template;
    this.headerBlocks = this.parseContent(template.header || '');
    this.bodyBlocks = this.parseContent(template.bodyContent || '');
    this.loadTemplateKeys(template.templateKey);

  }

  async loadTemplateKeys(templateKey: string): Promise<void> {
    try {
      this.templateKeys = await this.getKeysByTemplate(templateKey);
      this._toastService.success('Template keys loaded successfully!', 'Success');
    } catch (error) {
      this._toastService.error('Failed to load template keys.', 'Error');
      console.error('Error loading template keys:', error);
    }
  }

  async getEmailTemplates(): Promise<any[]> {
    this._loaderService.showLoader();
    const apiService = this.createApiService();
    const req$ = apiService.get(
      AppConstants.CONTROLLERS.Common,
      AppConstants.ACTIONS.GetTemplates,
      true,
      environment.APIs_PREFIX
    );

    try {
      const data: any = await lastValueFrom(req$);
      return data?.data || [];
    } catch (err: any) {
      throw err;
    } finally {
      this._loaderService.hideLoader();
    }
  }

  async getKeysByTemplate(templateKey: string): Promise<any[]> {
    this._loaderService.showLoader();
    const apiService = this.createApiService();
    const req$ = apiService.get(
      `${AppConstants.CONTROLLERS.Common}/${AppConstants.ACTIONS.GetKeysByTemplate}/${templateKey}`,
      '',
      true,
      environment.APIs_PREFIX
    );

    try {
      const data: any = await lastValueFrom(req$);
      return data?.data || [];
    } catch (err: any) {
      throw err;
    } finally {
      this._loaderService.hideLoader();
    }
  }

  filterKeys(content: string): string {
    const container = document.createElement('div');
    container.innerHTML = content; // Parse the content into a DOM structure
  
    // Recursively traverse and clean the DOM nodes
    const traverseAndClean = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Return plain text nodes
        return node.textContent || '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
  
        // If the element is a key block, keep its text content
        if (element.classList.contains('key-block')) {
          return element.textContent || '';
        }
  
        // If the element is an HTML tag, retain its structure
        const tagName = element.tagName.toLowerCase();
        const innerHTML = Array.from(element.childNodes).map(traverseAndClean).join('');
  
        // Retain the tag but clean its children
        return `<${tagName}>${innerHTML}</${tagName}>`;
      }
  
      return ''; // Ignore other node types
    };
  
    // Process the child nodes of the container and join them into a single string
    return Array.from(container.childNodes).map(traverseAndClean).join('');
  }
  
  testEmailTemplate(): void {
    if (!this.selectedTemplate) {
        this._toastService.error("No template selected for testing.", "Error");
        return;
    }

   
    var payload;
    const headerContainer = document.getElementById('header');
    const bodyContentContainer = document.getElementById('bodyContent');
    if (headerContainer && bodyContentContainer ) {
       payload = {
        header: headerContainer.textContent  || '',
        bodyContent:  bodyContentContainer.textContent  || '',
      };
    }
 

  
    this._loaderService.showLoader();
    this.sendEmailTest(payload)
        .then((response: any) => {
            if (response?.isSuccess) {
                this._toastService.success("Test email sent successfully!", "Success");
            } else {
                this._toastService.error(response?.errorMessageEn || "Failed to send test email.", "Error");
            }
        })
        .catch((err: any) => {
            console.error("Failed to send test email:", err);
            this._toastService.error("Failed to send test email due to an unexpected error.", "Error");
        })
        .finally(() => {
            this._loaderService.hideLoader();
        });
}

private sendEmailTest(payload: any): Promise<any> {
    const apiService = this.createApiService();
    const req$ = apiService.post(
        AppConstants.CONTROLLERS.Common,
        AppConstants.ACTIONS.SendEmailTest,
        true,
        environment.APIs_PREFIX,
        payload
    );
    return lastValueFrom(req$);
}


  async updateEmailTemplate(template: any): Promise<void> {
    this._loaderService.showLoader();
    const apiService = this.createApiService();
    const headerContainer = document.getElementById('header');
    const bodyContentContainer = document.getElementById('bodyContent');
    if (headerContainer) {
      template.header = headerContainer.textContent  || ''; // Populate the header content
    }
    if (bodyContentContainer) {
      template.bodyContent =  bodyContentContainer.textContent  || ''; // Populate the body content
    }

    // Use Promise-based chaining with .then and .catch
    this.firePostEmailTemplate(apiService, template)
      .then((response: any) => {
        // Check if the response indicates success
        if (response?.isSuccess) {
          this._toastService.success('Template updated successfully!', 'Success');
        } else {
          const errorMessage = response?.errorMessageEn || 'Template update failed.';
          this._toastService.error(errorMessage, 'Error');
        }
      })
      .catch((error: any) => {
        // Handle any errors that occurred during the request
        console.error('Failed to update email template:', error);
        this._toastService.error('Failed to update email template due to an unexpected error.', 'Error');
      })
      .finally(() => {
        // Hide the loader regardless of success or failure
        this._loaderService.hideLoader();
      });
  }
  
  // Helper function for making the API call
  private firePostEmailTemplate(apiService: ApiService, payload: any): Promise<any> {
    const req$ = apiService.post(
      AppConstants.CONTROLLERS.Common,
      AppConstants.ACTIONS.UpdateEmailTemplate,
      true,
      environment.APIs_PREFIX,
      payload
    );
    return lastValueFrom(req$);
  }
  
  getPlainTextFromField(field: string): string {
    const container = document.getElementById(field);
    if (!container) return '';
  
    return Array.from(container.childNodes)
      .map(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          // Include plain text
          return node.textContent || '';
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          if (element.classList.contains('key-block')) {
            // Include key-block spans
            return element.textContent || '';
          } else {
            // Flatten other HTML elements' content
            return element.innerHTML || '';
          }
        }
        return '';
      })
      .join('');
  }
  

  

    //#region handle keys
 
    insertKeyAtCursor(key: string): void {
      if (!this.savedRange || !this.currentField) {
        console.error('Cursor position not saved or no field selected.');
        return;
      }
    
      // Get the current field container
      const container = document.getElementById(this.currentField);
      if (!container) {
        console.error('Field container not found.');
        return;
      }
      
      // Dynamically create a span for the key
      const span = this.renderer.createElement('span');
      this.renderer.addClass(span, 'key-block');
      this.renderer.setAttribute(span, 'contenteditable', 'false');
      this.renderer.setProperty(span, 'textContent', ` {{${key}}} `);
    
      span.addEventListener('dblclick', () => this.removeKey(span));
    
      // Insert the key block at the saved cursor position
      this.savedRange.deleteContents(); // Clear selected content
      this.savedRange.insertNode(span);
      this.savedRange.collapse(false); // Move cursor after the inserted node
    
      // Restore the cursor after insertion
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(this.savedRange);
      }

    }



    
    extractPlainTextFromContainer(container: HTMLElement): string {
      let result = '';
    
      // Iterate through all child nodes of the container
      container.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          // Plain text node
          result += node.textContent;
        } else if (
          node.nodeType === Node.ELEMENT_NODE &&
          (node as HTMLElement).classList.contains('key-block')
        ) {
          // Replace key block with its text content (e.g., {{key}})
          result += (node as HTMLElement).textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // For other elements, include their inner text
          result += (node as HTMLElement).innerText;
        }
      });
    
      return result.trim(); // Return the cleaned plain text
    }
    

    
    
    getContentFromField(field: string): string {
      const container = document.getElementById(field);
      if (!container) return '';
    
      return container.innerHTML; // Return the full HTML content
    }
    
    

  

  


    
  
    parseContent(content: string): Array<{ type: 'text' | 'key'; content: string }> {
      const regex = /{{(.*?)}}/g;
      const blocks: Array<{ type: 'text' | 'key'; content: string }> = [];
      let lastIndex = 0;
      let result;
  
      while ((result = regex.exec(content)) !== null) {
        if (result.index > lastIndex) {
          blocks.push({ type: 'text', content: content.substring(lastIndex, result.index) });
        }
        blocks.push({ type: 'key', content: `{{${result[1]}}}` });
        lastIndex = regex.lastIndex;
      }
  
      if (lastIndex < content.length) {
        blocks.push({ type: 'text', content: content.substring(lastIndex) });
      }
  
      return blocks;
    }
  
    removeKey(target: EventTarget | null): void {
      // Ensure the target is an HTMLElement
      if (!(target instanceof HTMLElement)) {
        console.error('Invalid target for key removal.');
        return;
      }
    
      // Remove the key block element
      target.remove();
    
  
   
    }
    
    

      
    //#endregion

  }






